import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { Polygon } from "geojson";

export const getSitemapVersionLevelsByIds = (sitemapVersionsIds: string[], sitemaps: Sitemap[], level: number) => {
    const sitemapVersionLevels = sitemaps.flatMap((sm) =>
        sm.versions
            .filter((v) => v.sitemapVersionLevels.length > 0 && sitemapVersionsIds.includes(v.id))
            .map((smv) => {
                const version = sm.versions.find((v) => v.id === smv.id);
                const sitemapVersionLevel = smv.sitemapVersionLevels.find((mapLevel) => mapLevel.levelIndex === level);
                const sitemapVersionLevelId = sitemapVersionLevel?.id ?? smv.sitemapVersionLevels[0]?.id;
                return { id: sitemapVersionLevelId, perimeter: sitemapVersionLevel?.perimeter, type: version.type };
            })
    );
    return sitemapVersionLevels;
};

// Get the sitemap version levels based on the provided sitemap configurations
// If a sitemap exists on this map that is not in the config, it will use the latest sitemap version by date
// If a sitemap version exists in the config but not in the sitemap, the latest version by date will be used
// Any sitemaps in the config with visible set to false will be ignored
export const getSitemapVersionLevelsByConfig = (sitemapConfigs: MapSitemapConfig[], sitemaps: Sitemap[], level: number) => {
    const configSitemapIds = sitemapConfigs.filter((cfg) => cfg.version2dVisible || cfg.version3dVisible).map((cfg) => cfg.sitemapId);

    // Helper to get version levels for a given versionId
    const getVersionLevel = (sitemap: Sitemap, versionId: string, level: number) => {
        let version = sitemap.versions.find((v) => v.id === versionId);
        if (!version) {
            // Fallback: pick the latest version by date
            [version] = sitemap.versions.filter((v) => v.sitemapVersionLevels.length > 0 && v.date).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        }
        if (!version || !version.sitemapVersionLevels.length) return null;
        const levelObj = version.sitemapVersionLevels.find((lvl) => lvl.levelIndex === level);
        return levelObj ? { id: levelObj.id, perimeter: levelObj.perimeter, type: version.type } : null;
    };

    // Get config version levels for both 2D and 3D
    const configVersionLevels = sitemapConfigs
        .filter((cfg) => cfg.version2dVisible || cfg.version3dVisible)
        .flatMap((cfg) => {
            const sitemap = sitemaps.find((sm) => sm.id === cfg.sitemapId);
            if (!sitemap) return [];
            const levels: { id: string; perimeter: Polygon; type: SitemapVersionType }[] = [];
            if (cfg.version2dVisible) {
                const level2d = getVersionLevel(sitemap, cfg.version2dId, level);
                if (level2d) levels.push(level2d);
            }
            if (cfg.version3dVisible) {
                const level3d = getVersionLevel(sitemap, cfg.version3dId, level);
                if (level3d) levels.push(level3d);
            }
            return levels;
        });

    const nonConfigVersionLevels = sitemaps
        .filter((sm) => !configSitemapIds.includes(sm.id))
        .flatMap((sm) => {
            const levels: { id: string; perimeter: Polygon; type: SitemapVersionType }[] = [];
            const latest2dVersion = sm.versions
                .filter((v) => v.sitemapVersionLevels.length > 0 && v.date && v.type === SitemapVersionType.TwoDimensional)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
            if (latest2dVersion) {
                const sitemapVersion2dLevel = latest2dVersion.sitemapVersionLevels.find((mapLevel) => mapLevel.levelIndex === level);
                if (sitemapVersion2dLevel) {
                    levels.push({ id: sitemapVersion2dLevel.id, perimeter: sitemapVersion2dLevel.perimeter, type: SitemapVersionType.TwoDimensional });
                }
            }
            const latest3dVersion = sm.versions
                .filter((v) => v.sitemapVersionLevels.length > 0 && v.date && v.type === SitemapVersionType.ThreeDimensional)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
            if (latest3dVersion) {
                const sitemapVersion3dLevel = latest3dVersion.sitemapVersionLevels.find((mapLevel) => mapLevel.levelIndex === level);
                if (sitemapVersion3dLevel) {
                    levels.push({ id: sitemapVersion3dLevel.id, perimeter: sitemapVersion3dLevel.perimeter, type: SitemapVersionType.ThreeDimensional });
                }
            }
            return levels;
        });
    return [...configVersionLevels, ...nonConfigVersionLevels];
};
